import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Search,
  DollarSign,
  ArrowLeft,
  Receipt,
} from 'lucide-react';
import { SearchSelect } from '../../../components/shared';
import DataTable from '../../../components/shared/DataTable';
import DatePicker from '../../../components/client/DatePicker';
import { getAdminSubscriptionPayments } from '../../../services/subscriptionService';

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
];

const AdminSubscriptionPayments = () => {
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    from: '',
    to: '',
    page: 1,
    per_page: 25,
  });

  const queryParams = useMemo(() => {
    const p = {
      page: filters.page,
      per_page: filters.per_page,
      sort_by: 'paid_at',
      sort_order: 'desc',
    };
    if (filters.status) p.status = filters.status;
    if (filters.search.trim()) p.search = filters.search.trim();
    if (filters.from) p.from = filters.from;
    if (filters.to) p.to = filters.to;
    return p;
  }, [filters]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-subscription-payments', queryParams],
    queryFn: async () => getAdminSubscriptionPayments(queryParams),
    placeholderData: (prev) => prev,
  });

  const pagination = data?.data;
  const rows = pagination?.data ?? [];

  const statusBadge = (status) => {
    const map = {
      completed: { cls: 'bg-green-100 text-green-800', Icon: CheckCircle },
      pending: { cls: 'bg-amber-100 text-amber-900', Icon: Clock },
      failed: { cls: 'bg-red-100 text-red-800', Icon: XCircle },
      refunded: { cls: 'bg-gray-100 text-gray-800', Icon: RefreshCw },
    };
    const cfg = map[status] || map.pending;
    const Icon = cfg.Icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
        <Icon className="w-3 h-3" />
        {status || 'unknown'}
      </span>
    );
  };

  const columns = [
    {
      key: 'paid_at',
      label: 'Paid',
      render: (row) => (
        <div className="text-sm">
          <div className="font-medium text-gray-900">{row.paid_at ? formatDt(row.paid_at) : '—'}</div>
          <div className="text-xs text-gray-500 font-mono">#{row.id}</div>
        </div>
      ),
    },
    {
      key: 'vendor',
      label: 'Vendor',
      render: (row) => {
        const v = row.vendor_subscription?.vendor;
        const name = v ? (v.name && String(v.name).trim() ? v.name : v.email) : '—';
        const vid = row.vendor_subscription?.vendor_id;
        return (
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
            <p className="text-xs text-gray-500 truncate">{v?.email}</p>
            {vid && (
              <Link to={`/admin/vendors/${vid}`} className="text-xs text-primary-600 hover:underline font-medium">
                View vendor
              </Link>
            )}
          </div>
        );
      },
    },
    {
      key: 'plan',
      label: 'Plan',
      render: (row) => (
        <span className="text-sm text-gray-800">{row.vendor_subscription?.subscription?.name || '—'}</span>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (row) => (
        <span className="text-sm font-semibold text-gray-900">
          {(row.currency || 'usd').toUpperCase()} {Number(row.amount || 0).toFixed(2)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => statusBadge(row.status),
    },
    {
      key: 'method',
      label: 'Method / Reference',
      render: (row) => (
        <div className="text-xs font-mono text-gray-700 space-y-0.5 max-w-[200px]">
          <div className="capitalize">{row.payment_method || '—'}</div>
          <div className="truncate text-gray-500" title={row.payment_reference}>
            {row.payment_reference || row.transaction_id || '—'}
          </div>
        </div>
      ),
    },
  ];

  const totalPaid =
    rows.filter((r) => r.status === 'completed').reduce((s, r) => s + Number(r.amount || 0), 0) ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link to="/admin/subscriptions" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Subscription plans
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="w-7 h-7 text-primary-600" />
            Subscription payments
          </h1>
          <p className="text-gray-600 mt-1 text-sm max-w-2xl">
            All vendor plan charges recorded in <code className="text-xs bg-gray-100 px-1 rounded">subscription_payments</code>,
            linked to active vendor subscriptions.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary-50 text-primary-700">
            <DollarSign className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">On this page (completed)</p>
            <p className="text-lg font-bold text-gray-900">${totalPaid.toFixed(2)}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gray-100 text-gray-700">
            <CreditCard className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Rows</p>
            <p className="text-lg font-bold text-gray-900">{pagination?.total ?? rows.length}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <SearchSelect
              label="Status"
              options={statusOptions}
              value={filters.status}
              onChange={(v) => setFilters((f) => ({ ...f, status: v, page: 1 }))}
              searchable={false}
              placeholder="Filter"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="search"
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))}
                placeholder="Email, reference, transaction…"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-black focus:border-black"
              />
            </div>
          </div>
          <DatePicker
            label="Paid from"
            value={filters.from}
            onChange={(v) => setFilters((f) => ({ ...f, from: v || '', page: 1 }))}
          />
          <DatePicker
            label="Paid to"
            value={filters.to}
            onChange={(v) => setFilters((f) => ({ ...f, to: v || '', page: 1 }))}
          />
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3">
            {error.message || 'Failed to load subscription payments.'}
          </div>
        )}

        <DataTable
          columns={columns}
          data={rows}
          loading={isLoading && !data}
          searchable={false}
          filterable={false}
          sortable={false}
          showPagination={false}
          emptyMessage="No subscription payments match your filters."
        />

        {pagination && pagination.last_page > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-100">
            <p className="text-sm text-gray-600">
              Page {pagination.current_page} of {pagination.last_page}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={pagination.current_page <= 1}
                onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, f.page - 1) }))}
                className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={pagination.current_page >= pagination.last_page}
                onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function formatDt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default AdminSubscriptionPayments;
